/**
 * Dictionary merging (spec §4.8). Pure — importable from tests without React.
 */
import type { Lang } from "@shared/api";

export type Dictionary = Record<string, string>;
export type LocalizedDictionary = { [L in Lang]: Dictionary };

/** Later dictionaries win; `duplicateKeys` reports the overlaps. */
export function mergeDictionaries(
  dictionaries: readonly LocalizedDictionary[],
): LocalizedDictionary {
  const merged: LocalizedDictionary = { sv: {}, en: {} };
  for (const dict of dictionaries) {
    Object.assign(merged.sv, dict.sv);
    Object.assign(merged.en, dict.en);
  }
  return merged;
}

/** Keys defined by more than one dictionary — a registry smell, asserted in tests. */
export function duplicateKeys(dictionaries: readonly LocalizedDictionary[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const dict of dictionaries) {
    for (const key of Object.keys(dict.sv)) {
      if (seen.has(key)) duplicates.add(key);
      seen.add(key);
    }
  }
  return [...duplicates].sort();
}

/** Keys present in one language but not the other. */
export function keyParityGaps(dictionary: LocalizedDictionary): {
  onlyInSv: string[];
  onlyInEn: string[];
} {
  const sv = new Set(Object.keys(dictionary.sv));
  const en = new Set(Object.keys(dictionary.en));
  return {
    onlyInSv: [...sv].filter((k) => !en.has(k)).sort(),
    onlyInEn: [...en].filter((k) => !sv.has(k)).sort(),
  };
}

/**
 * The dictionary key a count selects: `progress.stats.streak.value.one` for
 * exactly one, `….other` for everything else (zero included). Swedish and
 * English share that rule, so one helper covers both languages — a real CLDR
 * plural table only pays for itself once a language with a "few"/"many"
 * category is added.
 */
export function pluralKey(key: string, count: number): string {
  return Math.abs(count) === 1 ? `${key}.one` : `${key}.other`;
}

/** `"Hej {name}!"` + `{ name: "Anna" }` → `"Hej Anna!"`. */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
}
